import React, { type JSX } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import type { ChatData } from "./useChatData";

const SPINNER_CIRCLE_PATH =
  "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z";
const CLIPBOARD_PATH =
  "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2";

/** The three bouncing dots shown while the bot is working. */
function BouncingDots(): JSX.Element {
  return (
    <div className="flex items-center space-x-1">
      <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:0ms]"></span>
      <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:150ms]"></span>
      <span className="w-2 h-2 bg-cvc-blue rounded-full animate-bounce [animation-delay:300ms]"></span>
    </div>
  );
}

/** Bouncing dots plus the current workflow step's friendly label. */
function WorkflowStatusIndicator({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex justify-start">
      <div className="w-full">
        <div className="flex items-center min-h-10">
          <div className="flex items-center space-x-3">
            <BouncingDots />
            <span className="text-sm text-cvc-text animate-[fadeInType_0.5s_ease-in] leading-none flex items-center -translate-y-0.5">
              {label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Tick, spinner or empty circle depending on the step's status. */
function StepMarker({ status }: { status?: string }): JSX.Element {
  if (status === "complete") {
    return <CheckCircleIcon className="w-4 h-4 text-cvc-green mt-0.5 shrink-0" />;
  }

  if (status === "in_progress") {
    return (
      <div className="mt-0.5 shrink-0">
        <svg className="w-4 h-4 text-cvc-blue animate-spin" fill="none" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path className="opacity-75" fill="currentColor" d={SPINNER_CIRCLE_PATH}></path>
        </svg>
      </div>
    );
  }

  return <div className="w-4 h-4 mt-0.5 shrink-0 rounded-full border-2 border-cvc-grey"></div>;
}

const STEP_TEXT_CLASSES: Record<string, string> = {
  in_progress: "text-cvc-blue font-medium",
  complete: "text-cvc-text/70",
};

/** Live checklist of the steps the agent plans to take this turn. */
function TurnPlanChecklist({ steps }: { steps: any[] }): JSX.Element {
  return (
    <div className="flex justify-start mt-3 mb-2">
      <div className="bg-white rounded-lg shadow-md max-w-md border border-gray-200">
        <div className="px-4 py-2 bg-cvc-blue rounded-t-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={CLIPBOARD_PATH}
              />
            </svg>
            <span className="text-sm font-semibold text-white">Plan</span>
          </div>
        </div>

        <div className="px-4 py-3 space-y-2">
          {steps.map((step, stepIndex) => (
            <div key={step.index || stepIndex} className="flex items-start space-x-2.5">
              <StepMarker status={step.status} />
              <span
                className={`text-sm leading-relaxed ${
                  STEP_TEXT_CLASSES[step.status as string] || "text-cvc-text"
                }`}
              >
                {step.friendlyName}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Generic typing indicator, used when there is no specific workflow status. */
function TypingDots(): JSX.Element {
  return (
    <div className="flex justify-start">
      <div className="w-full flex items-center min-h-10">
        <BouncingDots />
      </div>
    </div>
  );
}

/** Placeholder shown before any message exists. */
function EmptyState(): JSX.Element {
  return (
    <div className="flex justify-center items-center h-32">
      <p className="text-cvc-text/70 text-sm">
        No messages yet. Start a conversation about deals!
      </p>
    </div>
  );
}

/**
 * Everything rendered after the transcript: workflow status, turn plan, typing
 * dots and the empty state.
 */
export default function ChatActivity({ chat }: { chat: ChatData }): JSX.Element {
  const { currentWorkflowStatus: status, chatMessages, currentTurnTodoPlan } = chat;

  // Hide the status and plan once the chat response for this turn starts streaming.
  // Use turnId from the workflow status itself to avoid a stale currentTurnId in workspace mode.
  const responseStarted =
    !status ||
    chatMessages.some((msg) => msg.turnId === status.turnId && msg.sender === "chatbot");
  const steps = currentTurnTodoPlan?.steps ?? [];

  return (
    <>
      {/* An empty label can happen with out-of-order status updates */}
      {status?.visible && status.label && !responseStarted && (
        <WorkflowStatusIndicator label={status.label as string} />
      )}

      {status?.visible && steps.length > 0 && !responseStarted && (
        <TurnPlanChecklist steps={steps} />
      )}

      {chat.isBotTyping && !status && <TypingDots />}

      {chatMessages.length === 0 && <EmptyState />}
    </>
  );
}
